import { supabase } from "../supabase-client";
import type { Database } from "../types/database";

export type VerificationRequest =
  Database["public"]["Tables"]["lawyer_verification_requests"]["Row"];
export type VerificationRequestInsert =
  Database["public"]["Tables"]["lawyer_verification_requests"]["Insert"];
export type VerificationRequestUpdate =
  Database["public"]["Tables"]["lawyer_verification_requests"]["Update"];

/**
 * Create a verification request
 */
export async function createVerificationRequest(
  requestData: Omit<VerificationRequestInsert, "id" | "created_at">
) {
  const { data, error } = await supabase
    .from("lawyer_verification_requests")
    .insert(requestData)
    .select()
    .single();

  if (error) throw error;
  return data;
}

/**
 * Get verification request by lawyer ID
 */
export async function getVerificationRequest(lawyerId: string) {
  const { data, error } = await supabase
    .from("lawyer_verification_requests")
    .select("*")
    .eq("lawyer_id", lawyerId)
    .order("created_at", { ascending: false })
    .limit(1)
    .single();

  if (error) {
    if (error.code === "PGRST116") {
      // No rows returned
      return null;
    }
    throw error;
  }
  return data;
}

/**
 * Get all verification requests for a lawyer
 */
export async function getVerificationRequestHistory(lawyerId: string) {
  const { data, error } = await supabase
    .from("lawyer_verification_requests")
    .select("*")
    .eq("lawyer_id", lawyerId)
    .order("created_at", { ascending: false });

  if (error) throw error;
  return data;
}

/**
 * Check verification status
 */
export async function checkVerificationStatus(lawyerId: string): Promise<{
  is_verified: boolean;
  status: string | null;
  verification_date: string | null;
}> {
  // Get lawyer profile
  const { data: lawyerProfile, error: lawyerError } = await supabase
    .from("lawyer_profiles")
    .select("is_verified, verification_date")
    .eq("id", lawyerId)
    .single();

  if (lawyerError) throw lawyerError;

  // Get latest verification request
  const verificationRequest = await getVerificationRequest(lawyerId);

  return {
    is_verified: lawyerProfile.is_verified || false,
    status: verificationRequest?.status || null,
    verification_date: lawyerProfile.verification_date,
  };
}

/**
 * Auto-verify a lawyer (called by trigger, but can be called manually)
 */
export async function autoVerifyLawyer(lawyerId: string): Promise<boolean> {
  // Call the database function
  const { data, error } = await supabase.rpc("auto_verify_lawyer", {
    p_lawyer_id: lawyerId,
  });

  if (error) throw error;
  return data as boolean;
}

/** Standard rejection reasons for exequatur (extensible catalog) */
export const VERIFICATION_REJECTION_REASONS = {
  invalid_format: "invalid_format",
  duplicate: "duplicate",
  unverifiable: "unverifiable",
  manual_rejection: "manual_rejection",
  invalido: "inválido",
} as const;

/** Admin lawyers exequatur overview row (from view) */
export interface AdminLawyerExequaturRow {
  id: string;
  email: string | null;
  full_name: string | null;
  country: string | null;
  profile_created_at: string | null;
  bar_number: string | null;
  university: string | null;
  is_verified: boolean;
  verification_date: string | null;
  lawyer_profile_created_at: string | null;
  latest_verification_request_id: string | null;
  verification_status: string | null;
  verification_method: string | null;
  last_reviewed_at: string | null;
  reviewed_by: string | null;
  rejection_reason: string | null;
  verification_notes: string | null;
  auto_verified: boolean | null;
  auto_verification_score: number | null;
  exequatur_display_status: "no_enviado" | "pendiente" | "verificado" | "invalido" | "rechazado" | "en_revision";
}

/** Compute exequatur_display_status from lawyer profile + latest verification request */
function computeExequaturStatus(
  barNumber: string | null,
  lvrStatus: string | null,
  rejectionReason: string | null
): AdminLawyerExequaturRow["exequatur_display_status"] {
  if (!barNumber || barNumber.trim() === "") return "no_enviado";
  if (lvrStatus === "approved") return "verificado";
  if (lvrStatus === "rejected") {
    if (
      rejectionReason &&
      (rejectionReason.toLowerCase().includes("inválido") ||
        rejectionReason.toLowerCase().includes("invalid") ||
        rejectionReason.toLowerCase().includes("invalido"))
    )
      return "invalido";
    return "rechazado";
  }
  if (lvrStatus === "in_review") return "en_revision";
  return "pendiente";
}

/**
 * Get admin lawyers exequatur overview.
 * Uses direct query (profiles + lawyer_profiles + lawyer_verification_requests)
 * so it works even when admin_lawyers_exequatur_overview view is not yet migrated.
 */
export async function getAdminLawyersExequaturOverview(options?: {
  exequatur_status?: AdminLawyerExequaturRow["exequatur_display_status"];
  country?: string;
  auto_verified?: boolean;
  search?: string;
  limit?: number;
  offset?: number;
}): Promise<AdminLawyerExequaturRow[]> {
  // 1. Fetch all lawyers: profiles + lawyer_profiles
  let profileQuery = supabase
    .from("profiles")
    .select(
      `
      id,
      email,
      full_name,
      country,
      created_at
    `
    )
    .eq("user_type", "lawyer");

  if (options?.country) {
    profileQuery = profileQuery.eq("country", options.country);
  }
  // Search filter applied in-memory to include bar_number (in lawyer_profiles)
  // so we fetch more and filter below when search is provided
  if (!options?.search) {
    // No search: could add filters here if needed
  }

  profileQuery = profileQuery.order("created_at", { ascending: false });
  const limit = options?.limit ?? 500;
  const offset = options?.offset ?? 0;
  profileQuery = profileQuery.range(offset, offset + limit - 1);

  const { data: profilesData, error: profilesError } = await profileQuery;

  if (profilesError) throw profilesError;
  if (!profilesData?.length) return [];

  const profileIds = profilesData.map((p: { id: string }) => p.id);

  // Exclude admins (admins should not appear in lawyer exequatur list)
  const { data: adminIds } = await supabase
    .from("admin_users")
    .select("id")
    .eq("is_active", true);
  const adminIdSet = new Set((adminIds ?? []).map((a: { id: string }) => a.id));
  const lawyerProfileIds = profileIds.filter((id) => !adminIdSet.has(id));
  if (lawyerProfileIds.length === 0) return [];

  // 2. Fetch lawyer_profiles for these ids
  const { data: lawyerProfiles, error: lpError } = await supabase
    .from("lawyer_profiles")
    .select("id, bar_number, university, is_verified, verification_date, created_at")
    .in("id", lawyerProfileIds);

  if (lpError) throw lpError;
  const lpMap = new Map((lawyerProfiles ?? []).map((lp) => [lp.id, lp]));

  // 3. Fetch latest verification request per lawyer
  const { data: lvrData, error: lvrError } = await supabase
    .from("lawyer_verification_requests")
    .select("*")
    .in("lawyer_id", lawyerProfileIds)
    .order("created_at", { ascending: false });

  if (lvrError) throw lvrError;

  // Keep only latest per lawyer_id
  const latestLvrMap = new Map<string, VerificationRequest>();
  for (const row of lvrData ?? []) {
    if (!latestLvrMap.has(row.lawyer_id)) {
      latestLvrMap.set(row.lawyer_id, row);
    }
  }

  // 4. Build rows (only for lawyers who have lawyer_profile - inner join semantics)
  const rows: AdminLawyerExequaturRow[] = [];

  for (const p of profilesData) {
    if (adminIdSet.has(p.id)) continue; // Skip admins
    const lp = lpMap.get(p.id);
    if (!lp) continue; // Skip if no lawyer_profile (shouldn't happen for user_type=lawyer)

    const lvr = latestLvrMap.get(p.id);
    const barNumber = lp.bar_number ?? null;
    const exequaturStatus = computeExequaturStatus(
      barNumber,
      lvr?.status ?? null,
      lvr?.rejection_reason ?? null
    );

    // Filter by exequatur_status if specified
    if (options?.exequatur_status && exequaturStatus !== options.exequatur_status)
      continue;

    if (options?.auto_verified !== undefined && lvr) {
      if (lvr.auto_verified !== options.auto_verified) continue;
    }
    if (options?.search) {
      const s = options.search.trim().toLowerCase();
      const matchEmail = p.email?.toLowerCase().includes(s);
      const matchName = p.full_name?.toLowerCase().includes(s);
      const matchBar = barNumber?.toLowerCase().includes(s);
      if (!matchEmail && !matchName && !matchBar) continue;
    }

    rows.push({
      id: p.id,
      email: p.email ?? null,
      full_name: p.full_name ?? null,
      country: p.country ?? null,
      profile_created_at: p.created_at ?? null,
      bar_number: barNumber,
      university: lp.university ?? null,
      is_verified: lp.is_verified ?? false,
      verification_date: lp.verification_date ?? null,
      lawyer_profile_created_at: lp.created_at ?? null,
      latest_verification_request_id: lvr?.id ?? null,
      verification_status: lvr?.status ?? null,
      verification_method: lvr?.verification_method ?? null,
      last_reviewed_at: lvr?.reviewed_at ?? null,
      reviewed_by: lvr?.reviewed_by ?? null,
      rejection_reason: lvr?.rejection_reason ?? null,
      verification_notes: lvr?.notes ?? null,
      auto_verified: lvr?.auto_verified ?? null,
      auto_verification_score: lvr?.auto_verification_score ?? null,
      exequatur_display_status: exequaturStatus,
    });
  }

  // Sort by profile_created_at desc (already from query, but we filtered so re-sort)
  rows.sort((a, b) => {
    const da = a.profile_created_at || "";
    const db = b.profile_created_at || "";
    return db.localeCompare(da);
  });

  return rows;
}

/**
 * Update verification request status (admin function)
 */
export async function updateVerificationStatus(
  requestId: string,
  status: "pending" | "approved" | "rejected" | "in_review",
  reviewedBy: string,
  notes?: string,
  rejectionReason?: string
) {
  const updateData: VerificationRequestUpdate = {
    status,
    reviewed_by: reviewedBy,
    reviewed_at: new Date().toISOString(),
    notes,
    rejection_reason: rejectionReason,
  };

  const { data, error } = await supabase
    .from("lawyer_verification_requests")
    .update(updateData)
    .eq("id", requestId)
    .select()
    .single();

  if (error) throw error;

  // If approved, update lawyer profile
  if (status === "approved" && data) {
    await supabase
      .from("lawyer_profiles")
      .update({
        is_verified: true,
        verification_date: new Date().toISOString(),
      })
      .eq("id", data.lawyer_id);
  }

  // If rejected, mark lawyer as not verified
  if (status === "rejected" && data) {
    await supabase
      .from("lawyer_profiles")
      .update({
        is_verified: false,
        verification_date: null,
      })
      .eq("id", data.lawyer_id);
  }

  return data;
}

/**
 * Admin: Approve or reject a lawyer who has no verification request (e.g. no_enviado).
 * Creates a manual verification request and updates lawyer_profiles.
 */
export async function adminSetVerificationDirect(
  lawyerId: string,
  status: "approved" | "rejected",
  adminId: string,
  rejectionReason?: string
) {
  const { data: lp } = await supabase
    .from("lawyer_profiles")
    .select("id, bar_number, university")
    .eq("id", lawyerId)
    .single();

  if (!lp) throw new Error("Lawyer profile not found");

  const { data: profile } = await supabase
    .from("profiles")
    .select("email")
    .eq("id", lawyerId)
    .single();

  const email = profile?.email ?? "admin-verified@lawgentia.com";
  const barNumber = lp.bar_number ?? "ADMIN_MANUAL";
  const university = lp.university ?? "N/A";

  const { data: newReq, error: insertErr } = await supabase
    .from("lawyer_verification_requests")
    .insert({
      lawyer_id: lawyerId,
      bar_number: barNumber,
      university,
      email,
      verification_method: "manual",
      status,
      reviewed_by: adminId,
      reviewed_at: new Date().toISOString(),
      rejection_reason: status === "rejected" ? rejectionReason ?? null : null,
    })
    .select()
    .single();

  if (insertErr) throw insertErr;

  if (status === "approved") {
    await supabase
      .from("lawyer_profiles")
      .update({
        is_verified: true,
        verification_date: new Date().toISOString(),
      })
      .eq("id", lawyerId);
  } else {
    await supabase
      .from("lawyer_profiles")
      .update({
        is_verified: false,
        verification_date: null,
      })
      .eq("id", lawyerId);
  }

  return newReq;
}

/**
 * Get pending verification requests (admin function)
 */
export async function getPendingVerificationRequests(limit?: number) {
  let query = supabase
    .from("lawyer_verification_requests")
    .select(
      `
      *,
      lawyer_profiles!inner(
        id,
        bar_number,
        university,
        years_of_experience,
        profiles!inner(
          full_name,
          email,
          country,
          profile_image_url
        )
      )
    `
    )
    .eq("status", "pending")
    .order("created_at", { ascending: true });

  if (limit) {
    query = query.limit(limit);
  }

  const { data, error } = await query;

  if (error) throw error;
  return data;
}

/** Verification request with nested lawyer_profiles and profiles (from getAllVerificationRequests) */
export interface VerificationRequestWithProfile extends VerificationRequest {
  lawyer_profiles?: {
    id: string;
    bar_number: string | null;
    university: string | null;
    profiles?: {
      full_name: string | null;
      email: string | null;
      country: string | null;
    } | null;
  } | null;
}

/**
 * Get all verification requests (admin function)
 */
export async function getAllVerificationRequests(options?: {
  status?: string;
  limit?: number;
  offset?: number;
}): Promise<VerificationRequestWithProfile[] | null> {
  let query = supabase.from("lawyer_verification_requests").select(`
      *,
      lawyer_profiles!inner(
        id,
        bar_number,
        university,
        profiles!inner(
          full_name,
          email,
          country
        )
      )
    `);

  if (options?.status) {
    query = query.eq("status", options.status);
  }

  query = query.order("created_at", { ascending: false });

  if (options?.limit) {
    query = query.limit(options.limit);
  }

  if (options?.offset) {
    query = query.range(
      options.offset,
      options.offset + (options.limit || 10) - 1
    );
  }

  const { data, error } = await query;

  if (error) throw error;
  return data;
}

/**
 * Resubmit verification request
 */
export async function resubmitVerificationRequest(
  lawyerId: string,
  updatedData: {
    bar_number: string;
    university: string;
    email: string;
    document_url?: string;
  }
) {
  const { data, error } = await supabase
    .from("lawyer_verification_requests")
    .insert({
      lawyer_id: lawyerId,
      ...updatedData,
      status: "pending",
      verification_method: updatedData.document_url ? "document" : "email",
    })
    .select()
    .single();

  if (error) throw error;
  return data;
}

/**
 * Validate Exequátur/bar number format (country-specific validation)
 * For Dominican Republic: format is 001-1234567-1 (XXX-XXXXXXX-X)
 */
export function validateBarNumber(
  barNumber: string,
  country: string
): { valid: boolean; error?: string } {
  if (!barNumber || barNumber.trim().length === 0) {
    return { valid: false, error: "exequatur.errors.required" };
  }

  // Country-specific validation
  switch (country.toUpperCase()) {
    case "RD":
    case "DO": {
      // Dominican Republic Exequátur format: 001-1234567-1 (XXX-XXXXXXX-X)
      const rdPattern = /^\d{3}-\d{7}-\d$/;
      if (!rdPattern.test(barNumber)) {
        return { valid: false, error: "exequatur.errors.invalidFormatRD" };
      }
      return { valid: true };
    }
    case "MX": {
      // Mexico: MX-123456 format
      const mxPattern = /^[A-Z]{2,4}-\d{6,10}$/i;
      if (!mxPattern.test(barNumber)) {
        return { valid: false, error: "exequatur.errors.invalidFormat" };
      }
      return { valid: true };
    }
    case "CO": {
      // Colombia: 6-10 digit number
      const coPattern = /^\d{6,10}$/;
      if (!coPattern.test(barNumber)) {
        return { valid: false, error: "exequatur.errors.invalidFormat" };
      }
      return { valid: true };
    }

    default:
      // Default: just check if it has at least 5 characters and contains numbers
      if (barNumber.length < 5 || !/\d/.test(barNumber)) {
        return { valid: false, error: "exequatur.errors.invalidFormat" };
      }
      return { valid: true };
  }
}

/**
 * Calculate verification score
 */
export function calculateVerificationScore(data: {
  email: string;
  bar_number: string;
  university: string;
  years_of_experience?: number;
}): number {
  let score = 0;

  // Email válido (cualquier email con @): +30
  if (data.email && data.email.includes("@")) {
    score += 30;
  }

  // Valid bar number: +40
  if (
    data.bar_number &&
    data.bar_number.length > 5 &&
    /\d/.test(data.bar_number)
  ) {
    score += 40;
  }

  // University filled: +20
  if (data.university && data.university.length > 3) {
    score += 20;
  }

  // Experience: +10
  if (data.years_of_experience && data.years_of_experience > 0) {
    score += 10;
  }

  return Math.min(score, 100);
}
